import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Blocks, FileCheck2, LayoutDashboard, Palette, Receipt, Save, ScrollText, Settings2 } from 'lucide-react';
import { useEffect, useId, useRef, useState, type FormEvent, type JSX, type ReactNode } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';

import { LeaveGuard } from '@/components/LeaveGuard';
import { Select } from '@/components/ui/Select';
import { TabPanel, Tabs, type TabItem } from '@/components/ui/Tabs';
import { AuditLogViewer } from '@/features/audit/components/AuditLogViewer';
import { usePlatformPermissions } from '@/features/auth/hooks/usePlatformPermissions';
import { getTenantHealth, tenantHealthQueryKey } from '@/features/metrics/api/metrics.graphql';
import { HealthBadge } from '@/features/metrics/components/HealthBadge';
import { ApiError } from '@/lib/api';
import { formatMoney } from '@/lib/money';
import { checkNit, sanitizeSubdomain } from '@/lib/tenant-identity';

import styles from './EditTenantPage.module.scss';
import {
  getTenant,
  updateTenant,
  type TenantDetail,
  type UpdateTenantInput,
} from '../api/tenants.graphql';
import { TenantAdminInvitationPanel } from '../components/TenantAdminInvitationPanel';
import { TenantBillingTab } from '../components/TenantBillingTab';
import { TenantConfigTab } from '../components/TenantConfigTab';
import { TenantFactusTab } from '../components/TenantFactusTab';
import { TenantLifecyclePanel } from '../components/TenantLifecyclePanel';
import { TenantModulesTab } from '../components/TenantModulesTab';
import { TenantOnboardingPanel } from '../components/TenantOnboardingPanel';
import { TenantSubscriptionSection } from '../components/TenantSubscriptionSection';
import { TenantSummaryTab } from '../components/TenantSummaryTab';
import { useSubdomainAvailability } from '../hooks/useSubdomainAvailability';
import { COUNTRY_OPTIONS, PLAN_TIER_OPTIONS } from '../select-options';
import { statusLabel, TENANT_TABS, type TenantTab } from '../tenant-labels';

type TabId = TenantTab;

function isTab(value: string | null): value is TabId {
  return TENANT_TABS.some((tab) => tab === value);
}

interface GeneralForm {
  name: string;
  legalName: string;
  email: string;
  /** NIT con DV para mostrar y editar (`900123456-8`); solo la base si el tenant no tiene DV. */
  nit: string;
  subdomain: string;
  planTier: string;
  country: string;
}

type FieldErrors = Partial<Record<keyof GeneralForm, string>>;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function formFromTenant(tenant: TenantDetail): GeneralForm {
  return {
    name: tenant.name,
    legalName: tenant.legalName ?? '',
    email: tenant.email ?? '',
    nit: tenant.nit
      ? tenant.verificationDigit
        ? `${tenant.nit}-${tenant.verificationDigit}`
        : tenant.nit
      : '',
    subdomain: tenant.subdomain ?? '',
    planTier: tenant.planTier,
    country: tenant.country,
  };
}

/**
 * OS-1713 — solo los campos modificados. Vaciar un opcional envía `null` (el API lo borra); un
 * campo sin tocar no se envía y no se revalida (tenants con datos anteriores a OS-1712).
 */
export function buildTenantUpdate(
  initial: GeneralForm,
  form: GeneralForm,
): { input: UpdateTenantInput; errors: FieldErrors } {
  const input: UpdateTenantInput = {};
  const errors: FieldErrors = {};
  const changed = (key: keyof GeneralForm): boolean => form[key].trim() !== initial[key].trim();

  if (changed('name')) {
    if (!form.name.trim()) {
      errors.name = 'El nombre comercial es obligatorio.';
    } else {
      input.name = form.name.trim();
    }
  }
  if (changed('legalName')) {
    input.legalName = form.legalName.trim() || null;
  }
  if (changed('email')) {
    const email = form.email.trim();
    if (email && !EMAIL_PATTERN.test(email)) {
      errors.email = 'Escribe un correo válido, p. ej. contacto@empresa.com.';
    } else {
      input.email = email || null;
    }
  }
  if (changed('nit')) {
    if (!form.nit.trim()) {
      input.nit = null;
    } else {
      const nit = checkNit(form.nit);
      if (nit.ok) {
        input.nit = nit.nit;
        input.verificationDigit = nit.verificationDigit;
      } else {
        errors.nit = nit.message;
      }
    }
  }
  if (changed('subdomain')) {
    input.subdomain = form.subdomain || null;
  }
  if (changed('planTier')) {
    input.planTier = form.planTier;
  }
  if (changed('country')) {
    input.country = form.country;
  }
  return { input, errors };
}

const FIELD_OF_API: Record<string, keyof GeneralForm> = {
  name: 'name',
  legalName: 'legalName',
  email: 'email',
  nit: 'nit',
  verificationDigit: 'nit',
  subdomain: 'subdomain',
  planTier: 'planTier',
  country: 'country',
};

export function EditTenantPage(): JSX.Element {
  const { tenantId = '' } = useParams<{ tenantId: string }>();
  const qc = useQueryClient();
  const idPrefix = useId();
  // OS-1718 — la pestaña va en `?tab=`: los pasos del checklist enlazan directo a ella.
  const [params, setParams] = useSearchParams();
  const rawTab = params.get('tab');
  // OS-1722 — Facturación solo con `platform.billing.read` (SOPORTE no la ve).
  const { can: canTab } = usePlatformPermissions();
  const canReadBilling = canTab('platform.billing.read');
  // Facturación electrónica (Factus) por tenant: capacidad propia, solo plataforma.
  const canReadFactus = canTab('platform.billing.factus.read');
  // OS-1726 — Auditoría solo con `platform.audit.read`.
  const canReadAudit = canTab('platform.audit.read');
  // OS-1724 — salud en la cabecera (misma consulta que el Resumen, con `platform.reports.read`).
  const headerHealth = useQuery({
    queryKey: tenantHealthQueryKey(tenantId),
    queryFn: () => getTenantHealth(tenantId),
    enabled: Boolean(tenantId) && canTab('platform.reports.read'),
  });
  const tab: TabId =
    isTab(rawTab) &&
    (rawTab !== 'billing' || canReadBilling) &&
    (rawTab !== 'factus' || canReadFactus) &&
    (rawTab !== 'audit' || canReadAudit)
      ? rawTab
      : 'summary';
  const tabsRef = useRef<HTMLDivElement>(null);
  function setTab(next: TabId): void {
    setParams(
      (prev) => {
        const updated = new URLSearchParams(prev);
        // OS-1724 — la pestaña de entrada es el Resumen: solo ella va sin `?tab`.
        if (next === 'summary') {
          updated.delete('tab');
        } else {
          updated.set('tab', next);
        }
        return updated;
      },
      { replace: true },
    );
  }
  function goToTab(next: TabId): void {
    setTab(next);
    // OS-1725 C2 — `?.()`: no todos los entornos implementan scrollIntoView (jsdom no lo hace).
    tabsRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }
  // OS-1717 — datos comerciales con `platform.tenants.update`; configuración con `configure`.
  const { can } = usePlatformPermissions();
  const canUpdate = can('platform.tenants.update');
  const [initial, setInitial] = useState<GeneralForm | null>(null);
  const [form, setForm] = useState<GeneralForm | null>(null);
  const [loadedId, setLoadedId] = useState<string | null>(null);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [saved, setSaved] = useState(false);

  const {
    data: tenant,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['platform-tenant', tenantId],
    queryFn: () => getTenant(tenantId),
    enabled: Boolean(tenantId),
  });

  // El formulario se inicializa una vez por tenant: un refetch (p. ej. al guardar la pestaña de
  // configuración) no pisa lo que el usuario está editando.
  useEffect(() => {
    if (tenant && loadedId !== tenant.id) {
      const values = formFromTenant(tenant);
      setInitial(values);
      setForm(values);
      setLoadedId(tenant.id);
    }
  }, [tenant, loadedId]);

  const subdomainChanged = Boolean(form && initial && form.subdomain !== initial.subdomain);
  const subdomainStatus = useSubdomainAvailability(
    subdomainChanged ? (form?.subdomain ?? '') : '',
    tenantId,
  );
  const dirty = Boolean(
    form &&
    initial &&
    (Object.keys(form) as (keyof GeneralForm)[]).some(
      (key) => form[key].trim() !== initial[key].trim(),
    ),
  );

  const mutation = useMutation({
    mutationFn: (input: UpdateTenantInput) => updateTenant(tenantId, input),
    onSuccess: () => {
      setSaved(true);
      void refetch();
      void qc.invalidateQueries({ queryKey: ['platform-tenants'] });
    },
    onError: (err) => {
      const key = err instanceof ApiError && err.field ? FIELD_OF_API[err.field] : undefined;
      if (key) {
        setErrors({ [key]: err.message });
        document.getElementById(`${idPrefix}-${key}`)?.focus();
      }
    },
  });

  function updateForm(key: keyof GeneralForm, value: string): void {
    setSaved(false);
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
    setErrors((prev) => {
      if (!(key in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  function handleSubmit(e: FormEvent): void {
    e.preventDefault();
    if (!form || !initial) {
      return;
    }
    const { input, errors: found } = buildTenantUpdate(initial, form);
    if (subdomainChanged && form.subdomain) {
      if (subdomainStatus.state === 'unavailable' || subdomainStatus.state === 'error') {
        found.subdomain = subdomainStatus.message;
      } else if (subdomainStatus.state === 'checking') {
        found.subdomain = 'Espera a que termine la verificación del subdominio.';
      }
    }
    setErrors(found);
    const first = Object.keys(found)[0];
    if (first) {
      document.getElementById(`${idPrefix}-${first}`)?.focus();
      return;
    }
    if (Object.keys(input).length > 0) {
      // La nueva base es lo enviado, aunque el usuario siga editando mientras se guarda.
      const submitted = form;
      mutation.mutate(input, { onSuccess: () => setInitial(submitted) });
    }
  }

  function handleConfigUpdated(): void {
    void refetch();
    void qc.invalidateQueries({ queryKey: ['platform-tenants'] });
  }

  /**
   * OS-1722 — la razón social y el NIT de Facturación son los de Datos generales: tras guardar se
   * recarga el tenant y, si el formulario general no tiene cambios, se vuelve a cargar con ellos.
   */
  function handleBillingSaved(): void {
    void refetch().then(() => {
      if (!dirty) {setLoadedId(null);}
    });
    void qc.invalidateQueries({ queryKey: ['platform-tenants'] });
  }

  if (!tenantId) {
    return <Navigate to="/tenants" replace />;
  }

  const fieldProps = (
    key: keyof GeneralForm,
  ): { id: string; 'aria-invalid': boolean; 'aria-describedby': string | undefined } => ({
    id: `${idPrefix}-${key}`,
    'aria-invalid': Boolean(errors[key]),
    'aria-describedby': errors[key] ? `${idPrefix}-${key}-error` : undefined,
  });
  const fieldError = (key: keyof GeneralForm): ReactNode =>
    errors[key] ? (
      <span id={`${idPrefix}-${key}-error`} className={styles.fieldError} role="alert">
        {errors[key]}
      </span>
    ) : null;

  // OS-1725 C3 — mismas pestañas y permisos que antes; el patrón accesible vive en `Tabs`.
  const tabItems: Array<TabItem<TabId>> = [
    { id: 'summary', label: 'Resumen', icon: <LayoutDashboard size={15} aria-hidden="true" /> },
    {
      id: 'general',
      label: `Datos generales${dirty ? ' •' : ''}`,
      icon: <Settings2 size={15} aria-hidden="true" />,
    },
    { id: 'config', label: 'Configuración', icon: <Palette size={15} aria-hidden="true" /> },
    { id: 'modules', label: 'Módulos', icon: <Blocks size={15} aria-hidden="true" /> },
    ...(canReadBilling
      ? [{ id: 'billing' as const, label: 'Facturación', icon: <Receipt size={15} aria-hidden="true" /> }]
      : []),
    ...(canReadFactus
      ? [
          {
            id: 'factus' as const,
            label: 'Facturación electrónica (Factus)',
            icon: <FileCheck2 size={15} aria-hidden="true" />,
          },
        ]
      : []),
    ...(canReadAudit
      ? [{ id: 'audit' as const, label: 'Auditoría', icon: <ScrollText size={15} aria-hidden="true" /> }]
      : []),
  ];

  return (
    <div className={styles.page}>
      <LeaveGuard
        when={dirty && !mutation.isPending}
        title="Cambios sin guardar"
        message="Tienes cambios sin guardar en los datos del tenant. Si sales, se pierden."
      />
      <header className={styles.header}>
        <div className={styles.headerText}>
          <Link to="/tenants" className={styles.back}>
            <ArrowLeft size={16} aria-hidden="true" />
            Tenants
          </Link>
          <h1 className={styles.title}>
            {isLoading ? 'Cargando…' : (tenant?.name ?? 'Editar tenant')}
          </h1>
          <p className={styles.subtitle}>
            {tenant ? `Estado: ${statusLabel(tenant.planStatus)}. ` : ''}
            Ficha 360: resumen, datos, configuración, módulos, facturación y auditoría.
          </p>
          {tenant ? (
            <dl className={styles.headerFacts}>
              <div>
                <dt>Subdominio</dt>
                <dd>{tenant.subdomain ?? '—'}</dd>
              </div>
              {canReadBilling ? (
                <div>
                  <dt>{typeof tenant.potentialMrr === 'number' && tenant.potentialMrr > 0 ? 'MRR potencial' : 'MRR'}</dt>
                  <dd>
                    {formatMoney(
                      typeof tenant.potentialMrr === 'number' && tenant.potentialMrr > 0
                        ? tenant.potentialMrr
                        : (tenant.mrr ?? null),
                    )}
                  </dd>
                </div>
              ) : null}
              {headerHealth.data ? (
                <div>
                  <dt>Salud</dt>
                  <dd>
                    <HealthBadge health={headerHealth.data.health} />
                  </dd>
                </div>
              ) : null}
            </dl>
          ) : null}
        </div>
        {tab === 'general' && canUpdate ? (
          <button
            type="submit"
            form={`${idPrefix}-form`}
            className={styles.saveBtn}
            disabled={mutation.isPending || !form || !dirty}
          >
            <Save size={16} aria-hidden="true" />
            {mutation.isPending ? 'Guardando…' : 'Guardar cambios'}
          </button>
        ) : null}
      </header>

      {tenant ? (
        <TenantLifecyclePanel
          tenant={tenant}
          onChanged={handleConfigUpdated}
          onGoToBilling={canReadBilling ? () => goToTab('billing') : undefined}
        />
      ) : null}
      {tenant ? <TenantOnboardingPanel tenantId={tenant.id} onGoToTab={goToTab} /> : null}
      {tenant ? <TenantAdminInvitationPanel tenantId={tenant.id} /> : null}

      <Tabs
        items={tabItems}
        value={tab}
        onChange={setTab}
        label="Secciones de la ficha del tenant"
        idPrefix={`${idPrefix}-ficha`}
        className={styles.tabs}
        tabClassName={styles.tab}
        activeTabClassName={styles.tabActive}
        listRef={tabsRef}
      />

      <TabPanel idPrefix={`${idPrefix}-ficha`} value={tab} className={styles.content}>
        {isError ? (
          <p className={styles.error} role="alert">
            No se pudo cargar el tenant: {error.message}
          </p>
        ) : isLoading || !tenant || !form ? (
          <p className={styles.loading} role="status">Cargando…</p>
        ) : tab === 'summary' ? (
          <TenantSummaryTab tenant={tenant} onGoToTab={goToTab} />
        ) : tab === 'audit' ? (
          <AuditLogViewer fixedTenantId={tenantId} />
        ) : tab === 'billing' ? (
          <>
            <TenantBillingTab
              tenantId={tenantId}
              canWrite={can('platform.billing.write')}
              onSaved={handleBillingSaved}
            />
            <TenantSubscriptionSection
              tenantId={tenantId}
              canWrite={can('platform.billing.write')}
            />
          </>
        ) : tab === 'factus' ? (
          <TenantFactusTab tenantId={tenantId} canWrite={can('platform.billing.factus.write')} />
        ) : tab === 'modules' ? (
          <TenantModulesTab tenantId={tenantId} />
        ) : tab === 'config' ? (
          <TenantConfigTab
            tenantId={tenantId}
            tenant={tenant}
            onUpdated={handleConfigUpdated}
            readOnly={!can('platform.tenants.configure')}
          />
        ) : (
          <form id={`${idPrefix}-form`} className={styles.panel} onSubmit={handleSubmit} noValidate>
            <h2 className={styles.panelTitle}>Información del tenant</h2>
            {canUpdate ? null : (
              <p className={styles.fieldHint}>
                Solo lectura: tu rol no edita los datos comerciales del tenant.
              </p>
            )}
            <fieldset className={styles.readOnlyGroup} disabled={!canUpdate}>
            <div className={styles.grid}>
              <label className={styles.field}>
                Nombre comercial *
                <input
                  type="text"
                  {...fieldProps('name')}
                  value={form.name}
                  onChange={(e) => updateForm('name', e.target.value)}
                  placeholder="Mi Restaurante"
                />
                {fieldError('name')}
              </label>
              <label className={styles.field}>
                Razón social
                <input
                  type="text"
                  {...fieldProps('legalName')}
                  value={form.legalName}
                  onChange={(e) => updateForm('legalName', e.target.value)}
                  placeholder="Mi Restaurante S.A.S."
                />
                {fieldError('legalName')}
              </label>
              <label className={styles.field}>
                NIT (con dígito de verificación)
                <input
                  type="text"
                  inputMode="numeric"
                  {...fieldProps('nit')}
                  value={form.nit}
                  onChange={(e) => updateForm('nit', e.target.value)}
                  placeholder="900123456-8"
                />
                {fieldError('nit')}
                <span className={styles.fieldHint}>Déjalo vacío para borrarlo.</span>
              </label>
              <label className={styles.field}>
                Email de contacto
                <input
                  type="email"
                  {...fieldProps('email')}
                  value={form.email}
                  onChange={(e) => updateForm('email', e.target.value)}
                  placeholder="admin@empresa.com"
                />
                {fieldError('email')}
              </label>
              <label className={styles.field}>
                Subdominio
                <div className={styles.subdomain}>
                  <input
                    type="text"
                    {...fieldProps('subdomain')}
                    value={form.subdomain}
                    onChange={(e) => updateForm('subdomain', sanitizeSubdomain(e.target.value))}
                    placeholder="mi-restaurante"
                  />
                  <span>.opssynk.com</span>
                </div>
                {fieldError('subdomain') ??
                  (subdomainChanged && form.subdomain ? (
                    <span aria-live="polite" className={styles.fieldHint}>
                      {subdomainStatus.state === 'checking'
                        ? 'Verificando disponibilidad…'
                        : subdomainStatus.state === 'available'
                          ? 'Subdominio disponible.'
                          : subdomainStatus.state === 'unavailable' ||
                              subdomainStatus.state === 'error'
                            ? subdomainStatus.message
                            : null}
                    </span>
                  ) : null)}
              </label>
              <label className={styles.field}>
                Paquete comercial
                <Select
                  {...fieldProps('planTier')}
                  value={form.planTier}
                  onChange={(value) => updateForm('planTier', value)}
                  options={PLAN_TIER_OPTIONS}
                />
                {fieldError('planTier')}
                <span className={styles.fieldHint}>
                  Informativo: cambiar el paquete no habilita ni deshabilita módulos. Los módulos se
                  gestionan en la pestaña Módulos.
                </span>
              </label>
              <label className={styles.field}>
                País
                <Select
                  {...fieldProps('country')}
                  value={form.country}
                  onChange={(value) => updateForm('country', value)}
                  options={COUNTRY_OPTIONS}
                />
                {fieldError('country')}
              </label>
            </div>
            </fieldset>
            {mutation.error && !(mutation.error instanceof ApiError && mutation.error.field) ? (
              <p className={styles.error} role="alert">
                {mutation.error.message}
              </p>
            ) : null}
            <p className={styles.success} role="status">
              {saved && !dirty ? 'Cambios guardados.' : ''}
            </p>
          </form>
        )}
      </TabPanel>
    </div>
  );
}
